export function calculateShotExpectedScorePoint(
  distance,
  angle,
  shotPressure,
  setPlay,
  shotMethod,
  side
) {
  if (distance < 0) return 0;

  const intercept        = 2.82;
  const distanceCoeff    = -0.072;
  const angleCoeff       = -0.003;
  const mediumPressure   = -0.49;
  const highPressure     = -0.657;
  const setPlayVal       = 3.547;
  const division34       = -0.258;
  const leftFoot         = -0.1;
  const hand             = 0.138;
  const leftSide         = 0.021;
  const distanceSetPlay  = -0.053;
  const angleSetPlay     = -0.018;
  const leftFootLeftSide = -0.044;
  const handLeftSide     = -0.017;

  const odds =
    intercept +
    distance * (distanceCoeff + (setPlay === "Open Play" ? 0 : distanceSetPlay)) +
    angle    * (angleCoeff    + (setPlay === "Open Play" ? 0 : angleSetPlay)) +
    (shotPressure === "Medium" ? mediumPressure : 0) +
    (shotPressure === "High"   ? highPressure   : 0) +
    division34 +
    (setPlay === "Open Play"   ? 0 : setPlayVal) +
    (shotMethod === "Left foot" ? leftFoot : 0) +
    (shotMethod === "Hand"      ? hand     : 0) +
    (side === "Left"            ? leftSide : 0) +
    ((shotMethod === "Left foot" && side === "Left") ? leftFootLeftSide : 0) +
    ((shotMethod === "Hand"      && side === "Left") ? handLeftSide     : 0);

  return 1 / (1 + Math.exp(-odds));
}

// ─── POINT WITH WIND ───────────────────────────────────

export function calculateShotExpectedScorePointWithWind(
  distance,
  angle,
  shotPressure,
  setPlay,
  shotMethod,
  side,
  vertWind
) {
  if (distance < 0) return 0;

  // First get the base odds from the point‐only model:
  const baseProbability = calculateShotExpectedScorePoint(
    distance, angle, shotPressure, setPlay, shotMethod, side
  );
  const interceptOdds   = Math.log(baseProbability / (1 - baseProbability));

  // Wind term parameters:
  const windCoeff = 0.0006944;
  const windPower = 2.5;

  const windTerm =
    Math.sign(vertWind) *
    windCoeff *
    Math.pow(Math.abs(vertWind * 0.539987), windPower);

  const oddsWithWind = interceptOdds + windTerm;
  return 1 / (1 + Math.exp(-oddsWithWind));
}

// ─── GOAL SHOT (3 points) ─────────────────────────────

export function calculateShotExpectedScoreGoal(
  distance,
  angle,
  shotPressure,
  setPlay,
  shotMethod,
  side
) {
  if (distance < 0) return 0;

  const intercept      = 2.700434;
  const distanceCoeff  = -0.200377;
  const angleCoeff     = -0.022389;
  const mediumPressure = -0.333419;
  const highPressure   = -1.061093;
  const leftFoot       = -0.049769;
  const hand           = 0.452619;

  const odds =
    intercept +
    distance * distanceCoeff +
    angle    * angleCoeff +
    (shotPressure === "Medium" ? mediumPressure : 0) +
    (shotPressure === "High"   ? highPressure   : 0) +
    (shotMethod === "Left foot" ? leftFoot : 0) +
    (shotMethod === "Hand"      ? hand     : 0);

  // A goal is worth 3 points
  return 3 * (1 / (1 + Math.exp(-odds)));
}

// ─── GOAL‐POINT COMPONENT (1 point) ────────────────────

export function calculateShotExpectedScoreGoalPoint(
  distance,
  angle,
  shotPressure,
  setPlay,
  shotMethod,
  side
) {
  if (distance < 0) return 0;

  const intercept     = -4.43853;
  const distanceCoeff = 0.19886;

  const odds = intercept + distance * distanceCoeff;
  // Worth 1 point
  return 1 * (1 / (1 + Math.exp(-odds)));
}

// ─── WRAPPER: PICK THE RIGHT MODEL ────────────────────

/**
 * distance, angle: numbers
 * shotPressure, setPlay, shotMethod, side: strings
 * shotType: "Point attempt" | "2 Point attempt" | anything else = goal
 * vertWind: number or null
 */
export function calculateVerticalWind(windStrength, windAngle, shotAngle) {
  const parsedWindStrength = Number(windStrength);
  const parsedWindAngle = Number(windAngle);
  const parsedShotAngle = Number(shotAngle);

  if (
    !Number.isFinite(parsedWindStrength) ||
    parsedWindStrength === 0 ||
    !Number.isFinite(parsedWindAngle) ||
    !Number.isFinite(parsedShotAngle)
  ) {
    return null;
  }

  const shotDirectionRad = (parsedShotAngle * Math.PI) / 180;
  const windDirectionRad = (parsedWindAngle * Math.PI) / 180;
  return parsedWindStrength * Math.cos(windDirectionRad - shotDirectionRad);
}

export function calculateShotExpectedScoreNew(
  distance,
  angle,
  shotPressure,
  setPlay,
  shotMethod,
  side,
  shotType,
  vertWind = null,
  gameType = "Mens"
) {
  const normalizedGameType = gameType === "Ladies" ? "Ladies" : "Mens";
  const adjustedDistance =
    normalizedGameType === "Ladies" &&
    (shotType === "Point attempt" || shotType === "2 Point attempt")
      ? distance * 1.5
      : distance;

  if (shotType === "Point attempt") {
    // point or point+wind
    return vertWind == null
      ? calculateShotExpectedScorePoint(adjustedDistance, angle, shotPressure, setPlay, shotMethod, side)
      : calculateShotExpectedScorePointWithWind(adjustedDistance, angle, shotPressure, setPlay, shotMethod, side, vertWind);

  } else if (shotType === "2 Point attempt") {
    // double the point value
    const p = vertWind == null
      ? calculateShotExpectedScorePoint(adjustedDistance, angle, shotPressure, setPlay, shotMethod, side)
      : calculateShotExpectedScorePointWithWind(adjustedDistance, angle, shotPressure, setPlay, shotMethod, side, vertWind);
    return 2 * p;

  } else {
    // Goal attempt
    if (setPlay !== "Penalty") {
      const goalVal      = calculateShotExpectedScoreGoal(distance, angle, shotPressure, setPlay, shotMethod, side);
      const goalPointVal = calculateShotExpectedScoreGoalPoint(distance, angle, shotPressure, setPlay, shotMethod, side);
      return goalVal + goalPointVal;
    } else {
      // Penalty fixed at 2.1
      return 2.1;
    }
  }
}
